import React, { useState, useMemo, useContext, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Card, EmptyState, Button } from '../components/common';
import { mockCourses, mockInstructors, mockEnrollments, mockDoubts } from '../mockData';
import { Course, Lesson, CourseSection, UserMode, CourseModule, Doubt } from '../types';
import { AuthContext } from '../contexts/AuthContext';
import { useToast } from '../components/Toaster';

// Icons
const PlayIcon = () => <svg className="h-5 w-5 text-gray-500" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" /></svg>;
const LessonIcon = () => <svg className="h-5 w-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>;
const ChevronUpIcon = () => <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" /></svg>;
const StarIcon: React.FC<{filled?: boolean}> = ({filled = true}) => <svg className={`w-4 h-4 ${filled ? 'text-yellow-400' : 'text-gray-300'}`} fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path></svg>;
const CheckIcon = () => <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"></path></svg>;
const CheckCircleGreenIcon = () => <svg className="h-5 w-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;
const LockClosedIcon = () => <svg className="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" /></svg>;
const XIcon = () => <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>;
const PaperclipIcon = () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" /></svg>;
const ArrowRightIcon = () => <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5-5 5M6 12h12" /></svg>;


// Feature Icons
const VideoOnDemandIcon = () => <svg className="w-4 h-4 text-pink-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"></path></svg>;
const DownloadIcon = () => <svg className="w-4 h-4 text-pink-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path></svg>;
const LifetimeAccessIcon = () => <svg className="w-4 h-4 text-pink-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path></svg>;
const MobileTvIcon = () => <svg className="w-4 h-4 text-pink-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"></path></svg>;
const AssignmentsIcon = () => <svg className="w-4 h-4 text-pink-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"></path></svg>;
const CertificateIcon = () => <svg className="w-4 h-4 text-pink-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"></path></svg>;
const EnrolledIcon = () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path></svg>;
const DurationIcon = () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>;
const ChaptersIcon = () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path></svg>;
const VideoHoursIcon = () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>;
const LevelIcon = () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>;

const includesIcons: { [key: string]: React.ReactNode } = { "video": <VideoOnDemandIcon />, "resources": <DownloadIcon />, "lifetime": <LifetimeAccessIcon />, "mobile": <MobileTvIcon />, "assignments": <AssignmentsIcon />, "completion": <CertificateIcon />, };
const featuresIcons: { [key: string]: React.ReactNode } = { "enrolled": <EnrolledIcon />, "duration": <DurationIcon />, "chapters": <ChaptersIcon />, "videoHours": <VideoHoursIcon />, "level": <LevelIcon />, };

const formatDuration = (seconds: number) => `${Math.floor(seconds / 60).toString().padStart(2,'0')}:${(seconds % 60).toString().padStart(2,'0')}`;

const VideoPlayerModal: React.FC<{ lesson: Lesson; onClose: () => void; onLessonComplete: (lessonId: string) => void; }> = ({ lesson, onClose, onLessonComplete }) => {
    const toast = useToast();
    const [showQuiz, setShowQuiz] = useState(false);
    const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
    const [isCorrect, setIsCorrect] = useState<boolean | null>(null);
    const [videoEnded, setVideoEnded] = useState(false);
    const [quizAnswered, setQuizAnswered] = useState(false);
    const question = lesson.quiz?.questions[0];

    const handleVideoEnd = () => {
        setVideoEnded(true);
        if (question) {
            setShowQuiz(true);
        } else {
            // If no quiz, the "quiz" part is considered done
            setQuizAnswered(true);
        }
    };

    const handleAnswer = (option: string) => {
        if (selectedAnswer) return; // Prevent changing answer
        setSelectedAnswer(option);
        setIsCorrect(option === question!.correctAnswer);
        setQuizAnswered(true); // Mark quiz as attempted
    };
    
    const getButtonClass = (option: string) => {
        if (!selectedAnswer) return 'bg-gray-100 hover:bg-gray-200';
        if (option === question!.correctAnswer) return 'bg-green-500 text-white';
        if (option === selectedAnswer && !isCorrect) return 'bg-red-500 text-white';
        return 'bg-gray-100 text-gray-500 cursor-not-allowed';
    };

    const isCompletable = videoEnded && quizAnswered;

    const handleComplete = () => {
        if (isCompletable) {
            onLessonComplete(lesson.id);
            onClose();
        } else {
            if (!videoEnded) {
                toast.error("Please watch the entire video to continue.");
            } else if (!quizAnswered) {
                toast.error("Please answer the quiz to complete the lesson.");
            }
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
                <header className="flex justify-between items-center p-4 border-b">
                    <h3 className="text-lg font-bold text-gray-800">{lesson.title}</h3>
                    <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-200"><XIcon /></button>
                </header>
                <div className="p-4 flex-1 overflow-y-auto">
                    {!showQuiz ? (
                        <video 
                            key={lesson.id} 
                            className="w-full h-full object-contain" 
                            controls 
                            controlsList="nodownload"
                            onContextMenu={(e) => e.preventDefault()}
                            autoPlay 
                            onEnded={handleVideoEnd}
                        >
                            <source src={lesson.videoUrl} type="video/mp4" />
                            Your browser does not support the video tag.
                        </video>
                    ) : question ? (
                        <div className="p-4 space-y-6">
                            <h4 className="text-xl font-semibold text-center">{question.question}</h4>
                            <div className="grid grid-cols-1 gap-4">
                                {question.options.map(option => (
                                    <button
                                        key={option}
                                        onClick={() => handleAnswer(option)}
                                        disabled={!!selectedAnswer}
                                        className={`p-4 rounded-lg text-left transition-colors font-medium ${getButtonClass(option)}`}
                                    >
                                        {option}
                                    </button>
                                ))}
                            </div>
                            {selectedAnswer && (
                                <div className={`mt-4 text-center p-3 rounded-lg text-white ${isCorrect ? 'bg-green-600' : 'bg-red-600'}`}>
                                    {isCorrect ? 'Correct! Well done.' : `Not quite. The correct answer was: ${question.correctAnswer}`}
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="text-center p-8 flex flex-col items-center justify-center h-full">
                            <h4 className="text-xl font-semibold">Video complete!</h4>
                            <p className="text-gray-600 mt-2">You can now complete the lesson to continue.</p>
                        </div>
                    )}
                </div>
                 <footer className="p-4 border-t flex justify-end gap-2">
                    <Button variant="secondary" onClick={onClose}>Close</Button>
                    <Button 
                        variant="primary"
                        onClick={handleComplete} 
                        disabled={!isCompletable}
                        title={!isCompletable ? 'Please watch the video and complete the quiz to continue' : 'Mark this lesson as complete'}
                    >
                        Mark as Completed
                    </Button>
                </footer>
            </div>
        </div>
    );
};

const SectionQuizModal: React.FC<{ 
    quiz: NonNullable<CourseSection['quiz']>; 
    onClose: () => void;
    onQuizComplete: (quizId: string, score: number, total: number) => void;
}> = ({ quiz, onClose, onQuizComplete }) => {
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [answers, setAnswers] = useState<Record<string, string>>({});
    const [isSubmitted, setIsSubmitted] = useState(false);

    const currentQuestion = quiz.questions[currentQuestionIndex];
    
    const score = useMemo(() => {
        return quiz.questions.reduce((acc, question) => {
            return acc + (answers[question.id] === question.correctAnswer ? 1 : 0);
        }, 0);
    }, [answers, quiz.questions]);

    const handleAnswerSelect = (questionId: string, answer: string) => {
        setAnswers(prev => ({ ...prev, [questionId]: answer }));
    };

    const handleSubmit = () => {
        setIsSubmitted(true);
        onQuizComplete(quiz.id, score, quiz.questions.length);
    };

    const handleRetry = () => {
        setAnswers({});
        setCurrentQuestionIndex(0);
        setIsSubmitted(false);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
                <header className="flex justify-between items-center p-4 border-b">
                    <h3 className="text-lg font-bold text-gray-800">{quiz.title}</h3>
                    <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-200"><XIcon /></button>
                </header>
                
                <div className="p-6 flex-1 overflow-y-auto">
                    {isSubmitted ? (
                        <div className="text-center flex flex-col items-center justify-center h-full">
                            <h4 className="text-2xl font-bold">Quiz Complete!</h4>
                            <p className="text-lg text-gray-600 mt-2">Your Score:</p>
                            <p className="text-5xl font-extrabold text-primary my-4">{score} / {quiz.questions.length}</p>
                            <div className="flex gap-4 mt-6">
                                <Button variant="outline" onClick={handleRetry}>Retry Quiz</Button>
                                <Button onClick={onClose}>Close</Button>
                            </div>
                        </div>
                    ) : (
                        <div>
                            <div className="mb-4 text-sm text-gray-600 font-medium">
                                Question {currentQuestionIndex + 1} of {quiz.questions.length}
                            </div>
                            <h4 className="text-xl font-semibold mb-6">{currentQuestion.question}</h4>
                            <div className="space-y-3">
                                {currentQuestion.options.map(option => {
                                    const isSelected = answers[currentQuestion.id] === option;
                                    return (
                                        <button
                                            key={option}
                                            onClick={() => handleAnswerSelect(currentQuestion.id, option)}
                                            className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                                                isSelected ? 'border-primary bg-primary-50 ring-2 ring-primary' : 'border-gray-300 hover:border-primary'
                                            }`}
                                        >
                                            {option}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>

                {!isSubmitted && (
                    <footer className="p-4 border-t flex justify-between items-center">
                        <Button 
                            variant="secondary" 
                            onClick={() => setCurrentQuestionIndex(i => i - 1)}
                            disabled={currentQuestionIndex === 0}
                        >
                            Previous
                        </Button>
                        {currentQuestionIndex < quiz.questions.length - 1 ? (
                             <Button 
                                onClick={() => setCurrentQuestionIndex(i => i + 1)}
                                disabled={!answers[currentQuestion.id]}
                            >
                                Next
                            </Button>
                        ) : (
                            <Button 
                                onClick={handleSubmit}
                                disabled={Object.keys(answers).length !== quiz.questions.length}
                            >
                                Submit
                            </Button>
                        )}
                    </footer>
                )}
            </div>
        </div>
    );
};

const CourseCurriculum: React.FC<{
    course: Course;
    onLessonClick: (lesson: Lesson) => void;
    onQuizClick: (quiz: NonNullable<CourseSection['quiz']>) => void;
    userMode: UserMode | undefined;
    completedLessons: string[];
    completedQuizzes: Record<string, { score: number; total: number }>;
    allLessons: Lesson[];
}> = ({ course, onLessonClick, onQuizClick, userMode, completedLessons, completedQuizzes, allLessons }) => {
    const modules = course.modules || [];
    const [openModules, setOpenModules] = useState<string[]>([modules[0]?.id || '']);
    const [openWeeks, setOpenWeeks] = useState<string[]>([modules[0]?.sections[0]?.id || '']);
    
    const allWeeks = useMemo(() => course.modules?.flatMap(m => m.sections) || [], [course.modules]);
    
    const toggleModule = (id: string) => setOpenModules(prev => prev.includes(id) ? prev.filter(mId => mId !== id) : [...prev, id]);
    const toggleWeek = (id: string) => setOpenWeeks(prev => prev.includes(id) ? prev.filter(wId => wId !== id) : [...prev, id]);

    return (
        <Card className="p-6">
            <h2 className="text-2xl font-bold mb-6">Course Curriculum</h2>
            <div className="relative pl-8">
                {/* Timeline */}
                <div className="absolute top-0 bottom-0 left-4 w-0.5 bg-gray-200"></div>
                
                {modules.map((module, moduleIndex) => {
                    const isModuleOpen = openModules.includes(module.id);
                    const lessonsInModule = module.sections.reduce((acc, s) => acc + s.lessons.length, 0);

                    return (
                        <div key={module.id} className="relative mb-4">
                            <div className="absolute -left-8 top-1.5 w-8 h-8 bg-red-100 rounded-full flex items-center justify-center font-bold text-red-600 ring-4 ring-white">
                                {moduleIndex + 1}
                            </div>
                            <div className="bg-white rounded-lg border border-gray-200">
                                <button onClick={() => toggleModule(module.id)} className="w-full flex justify-between items-center p-4 text-left">
                                    <div>
                                        <h3 className="font-bold text-gray-800">{module.title}</h3>
                                        <p className="text-sm text-gray-500">{lessonsInModule} lessons</p>
                                    </div>
                                    <ChevronUpIcon />
                                </button>
                                {isModuleOpen && (
                                    <div className="px-4 pb-2">
                                        {module.sections.map((week, weekIndex) => {
                                            const isWeekOpen = openWeeks.includes(week.id);
                                            const quizInfo = week.quiz ? completedQuizzes[week.quiz.id] : null;
                                            const isQuizCompleted = !!quizInfo;
                                            const areAllWeekLessonsCompleted = week.lessons.every(l => completedLessons.includes(l.id));

                                            return (
                                                <div key={week.id} className="relative pl-10 py-2">
                                                    <div className="absolute left-0 top-3 w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center text-xs font-bold text-gray-600">
                                                        {weekIndex + 1}
                                                    </div>
                                                    <button onClick={() => toggleWeek(week.id)} className="w-full flex justify-between items-center p-2 text-left hover:bg-gray-50 rounded-md">
                                                        <h4 className="font-semibold text-gray-700">{week.title}</h4>
                                                        <ChevronUpIcon />
                                                    </button>
                                                    {isWeekOpen && (
                                                        <div className="pt-2">
                                                            <ul className="space-y-1 divide-y divide-gray-100">
                                                                {week.lessons.map(lesson => {
                                                                    const lessonGlobalIndex = allLessons.findIndex(l => l.id === lesson.id);
                                                                    const weekGlobalIndex = allWeeks.findIndex(w => w.id === week.id);
                                                                    
                                                                    const isCompleted = completedLessons.includes(lesson.id);

                                                                    let isLocked = false;
                                                                    // Rule 1: Previous lesson isn't complete
                                                                    if (lessonGlobalIndex > 0 && !completedLessons.includes(allLessons[lessonGlobalIndex - 1]?.id)) {
                                                                        isLocked = true;
                                                                    }
                                                                    // Rule 2: It's the first lesson of a week, and the previous week's quiz isn't complete
                                                                    if (!isLocked && lesson.id === week.lessons[0].id && weekGlobalIndex > 0) {
                                                                        const prevWeek = allWeeks[weekGlobalIndex - 1];
                                                                        if (prevWeek.quiz && !completedQuizzes[prevWeek.quiz.id]) {
                                                                            isLocked = true;
                                                                        }
                                                                    }

                                                                    const isPlayable = userMode === 'online' && lesson.videoUrl && !isLocked;

                                                                    const getLessonIcon = () => {
                                                                        if (isCompleted) return <CheckCircleGreenIcon />;
                                                                        if (isLocked) return <LockClosedIcon />;
                                                                        return userMode === 'online' && lesson.videoUrl ? <PlayIcon /> : <LessonIcon />;
                                                                    };
                                                                    
                                                                    return (
                                                                        <li key={lesson.id}>
                                                                            <button
                                                                                onClick={() => isPlayable && onLessonClick(lesson)}
                                                                                disabled={!isPlayable}
                                                                                className="w-full text-left p-3 hover:bg-gray-100 rounded-md flex items-center justify-between gap-3 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:opacity-50"
                                                                            >
                                                                                <div className="flex items-center gap-3">
                                                                                    {getLessonIcon()}
                                                                                    <span className={`text-sm font-medium ${isCompleted ? 'text-gray-500 line-through' : 'text-gray-800'}`}>{lesson.title}</span>
                                                                                </div>
                                                                                <span className="text-xs text-gray-500 font-mono">{formatDuration(lesson.duration)}</span>
                                                                            </button>
                                                                        </li>
                                                                    );
                                                                })}
                                                            </ul>
                                                             {week.quiz && (
                                                                <div className="mt-4 p-2">
                                                                    {isQuizCompleted ? (
                                                                        <div className="flex items-center gap-2 p-2 border border-green-200 bg-green-50 rounded-md text-sm font-medium text-green-700">
                                                                            <CheckCircleGreenIcon />
                                                                            <span>{week.quiz.title} &mdash; Score: {quizInfo.score}/{quizInfo.total}</span>
                                                                        </div>
                                                                    ) : (
                                                                        <Button 
                                                                            variant="outline" 
                                                                            size="sm" 
                                                                            onClick={() => onQuizClick(week.quiz!)}
                                                                            disabled={!areAllWeekLessonsCompleted}
                                                                            title={!areAllWeekLessonsCompleted ? "Complete all lessons in this week to unlock the quiz" : ""}
                                                                        >
                                                                            Take {week.quiz.title}
                                                                        </Button>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </Card>
    );
};


const InstructorCard: React.FC<{ instructorId?: string }> = ({ instructorId }) => {
    const instructor = mockInstructors.find(i => i.id === instructorId);
    if(!instructor) return null;

    return (
        <Card className="p-6">
            <h2 className="text-2xl font-bold mb-4">About the instructor</h2>
            <div className="flex items-start gap-4 mb-4">
                <img src={instructor.avatar} alt={instructor.name} className="w-20 h-20 rounded-full"/>
                <div>
                    <h3 className="text-lg font-bold">{instructor.name}</h3>
                    <p className="text-sm text-gray-600 mb-1">{instructor.title}</p>
                    <div className="flex items-center gap-1">
                        {[...Array(5)].map((_, i) => <StarIcon key={i} filled={i < Math.round(instructor.rating)} />)}
                        <span className="text-sm ml-1 font-bold text-gray-800">{instructor.rating}</span>
                    </div>
                </div>
            </div>
             <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm border-y py-4 my-4">
                 <div className="flex items-center gap-2"><CoursesIcon/> <span>{instructor.stats.courses} Courses</span></div>
                 <div className="flex items-center gap-2"><LessonsIcon/> <span>{instructor.stats.lessons} Lesson</span></div>
                 <div className="flex items-center gap-2"><DurationIcon/> <span>{instructor.stats.duration}</span></div>
                 <div className="flex items-center gap-2"><EnrolledIcon/> <span>{instructor.stats.students.toLocaleString()} students enrolled</span></div>
            </div>
            <p className="text-sm text-gray-600 mb-4">{instructor.bio}</p>
            <div className="mb-4">
                <h4 className="font-semibold mb-2">Skills:</h4>
                <div className="flex flex-wrap gap-2">
                    {instructor.skills.slice(0, 15).map(skill => <span key={skill} className="bg-gray-100 text-xs px-2.5 py-1 rounded-full">{skill}</span>)}
                </div>
            </div>
             <div>
                <h4 className="font-semibold mb-2">Available for:</h4>
                <ol className="text-sm text-gray-600 list-decimal list-inside space-y-1">
                    {instructor.availability.map(item => <li key={item}>{item}</li>)}
                </ol>
            </div>
        </Card>
    );
}

const AskDoubtsCard: React.FC<{ course: Course; lessons: Lesson[] }> = ({ course, lessons }) => {
    const auth = useContext(AuthContext);
    const toast = useToast();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [doubts, setDoubts] = useState<Doubt[]>(() => mockDoubts.filter(d => d.courseId === course.id).sort((a,b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()));
    const [newDoubtText, setNewDoubtText] = useState('');
    const [selectedLessonId, setSelectedLessonId] = useState<string>(lessons[0]?.id || '');
    const [attachedFile, setAttachedFile] = useState<File | null>(null);

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files[0]) {
            setAttachedFile(event.target.files[0]);
            toast.success(`Attached: ${event.target.files[0].name}`);
        }
    };

    const handleSendDoubt = () => {
        if (!newDoubtText.trim() || !auth?.currentUser) {
            toast.error("Please type your doubt before sending.");
            return;
        }

        const newDoubt: Doubt = {
            id: `D${Date.now()}`,
            courseId: course.id,
            lessonId: selectedLessonId,
            userId: auth.currentUser.id,
            userName: `${auth.currentUser.firstName} ${auth.currentUser.lastName}`,
            userAvatar: auth.currentUser.avatar || '',
            doubtText: newDoubtText,
            timestamp: new Date().toISOString(),
            replies: [],
            ...(attachedFile && { attachment: { name: attachedFile.name, url: '#' } }),
        };

        setDoubts(prev => [newDoubt, ...prev]);
        setNewDoubtText('');
        setAttachedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
        toast.success("Your doubt has been posted!");
    };

    const getLessonTitle = (lessonId: string) => {
        return lessons.find(l => l.id === lessonId)?.title || 'General Question';
    };

    return (
        <Card className="p-6 flex flex-col h-[42rem]">
            <h2 className="text-2xl font-bold mb-4 flex-shrink-0">Ask Doubts</h2>
            
            <div className="flex-shrink-0 pb-4 border-b border-gray-200">
                <div className="space-y-2">
                    <div>
                        <label className="text-sm font-medium text-gray-700">Course</label>
                        <p className="mt-1 w-full p-2 border border-gray-200 bg-gray-50 rounded-md text-sm text-gray-600">{course.title}</p>
                    </div>
                    <div>
                        <label htmlFor="lessonSelect" className="text-sm font-medium text-gray-700">Lesson</label>
                         <select id="lessonSelect" value={selectedLessonId} onChange={e => setSelectedLessonId(e.target.value)} className="mt-1 w-full p-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary text-sm">
                            {lessons.map(lesson => <option key={lesson.id} value={lesson.id}>{lesson.title}</option>)}
                        </select>
                    </div>
                </div>
                <textarea 
                    value={newDoubtText}
                    onChange={e => setNewDoubtText(e.target.value)}
                    placeholder="Type your doubt here..."
                    rows={3}
                    className="mt-2 w-full p-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                />
                <div className="flex justify-between items-center mt-2">
                    <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
                            <PaperclipIcon /> Attach file
                        </Button>
                        <input type="file" ref={fileInputRef} onChange={handleFileChange} className="hidden" />
                        {attachedFile && <span className="text-xs text-gray-500">{attachedFile.name}</span>}
                    </div>
                    <Button onClick={handleSendDoubt}>
                        Send <ArrowRightIcon />
                    </Button>
                </div>
            </div>
            
            <div className="flex-1 space-y-4 overflow-y-auto pr-2 mt-4">
                {doubts.map(doubt => (
                    <div key={doubt.id} className={`p-4 rounded-lg ${doubt.userId === auth?.currentUser?.id ? 'bg-primary-50' : 'bg-gray-50'}`}>
                        <div className="flex items-start gap-3">
                            <img src={doubt.userAvatar} alt={doubt.userName} className="w-10 h-10 rounded-full" />
                            <div className="flex-1">
                                <div className="flex justify-between items-baseline">
                                    <p className="font-bold text-gray-800">{doubt.userId === auth?.currentUser?.id ? 'You' : doubt.userName}</p>
                                    <p className="text-xs text-gray-500">{new Date(doubt.timestamp).toLocaleString()}</p>
                                </div>
                                <p className="text-xs text-gray-600 font-medium mb-2">Lesson: {getLessonTitle(doubt.lessonId)}</p>
                                <p className="text-sm text-gray-700 whitespace-pre-wrap">{doubt.doubtText}</p>
                                {doubt.attachment && (
                                    <div className="mt-2">
                                        <a href={doubt.attachment.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm text-blue-600 bg-blue-100 hover:bg-blue-200 px-2 py-1 rounded">
                                            <PaperclipIcon /> {doubt.attachment.name}
                                        </a>
                                    </div>
                                )}
                            </div>
                        </div>
                        {doubt.replies.map(reply => (
                            <div key={reply.id} className="mt-3 ml-8 pl-4 border-l-2 border-primary-200">
                                <div className="flex items-start gap-3">
                                    <img src={reply.authorAvatar} alt={reply.authorName} className="w-8 h-8 rounded-full" />
                                    <div className="flex-1">
                                        <div className="flex justify-between items-baseline">
                                            <p className="font-bold text-sm text-gray-800">{reply.authorName} <span className="text-xs font-normal text-primary bg-primary-100 px-1.5 py-0.5 rounded">Instructor</span></p>
                                            <p className="text-xs text-gray-500">{new Date(reply.timestamp).toLocaleString()}</p>
                                        </div>
                                        <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">{reply.replyText}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        </Card>
    );
};

const CoursesIcon = () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 10h16M4 14h16M4 18h16"></path></svg>;
const LessonsIcon = () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 4h2a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h2"></path><rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect></svg>;

const CourseDetailPage: React.FC = () => {
    const { courseId } = useParams<{ courseId: string }>();
    const course = useMemo(() => mockCourses.find(c => c.id === courseId), [courseId]);
    const auth = useContext(AuthContext);
    const toast = useToast();
    
    const [selectedLesson, setSelectedLesson] = useState<Lesson | null>(null);
    const [activeQuiz, setActiveQuiz] = useState<NonNullable<CourseSection['quiz']> | null>(null);
    const [completedQuizzes, setCompletedQuizzes] = useState<Record<string, { score: number; total: number }>>({});

    const userEnrollment = useMemo(() => 
        mockEnrollments.find(e => e.userId === auth?.currentUser?.id && e.courseId === courseId),
        [auth?.currentUser?.id, courseId]
    );

    const [completedLessons, setCompletedLessons] = useState<string[]>(userEnrollment?.completedLessonIds || []);

    const handleLessonComplete = (lessonId: string) => {
        setCompletedLessons(prev => {
            const newCompleted = Array.from(new Set([...prev, lessonId]));
             const enrollment = mockEnrollments.find(e => e.userId === auth?.currentUser?.id && e.courseId === courseId);
            if (enrollment) {
                enrollment.completedLessonIds = newCompleted;
            }
            return newCompleted;
        });
    };
    
    const handleQuizComplete = (quizId: string, score: number, total: number) => {
        setCompletedQuizzes(prev => ({ ...prev, [quizId]: { score, total } }));
        setActiveQuiz(null);
        toast.success(`Quiz completed! Score: ${score}/${total}`);
    };

    const allLessons = useMemo(() => {
        if (!course) return [];
        if (course.modules && course.modules.length > 0) {
            return course.modules.flatMap(m => m.sections).flatMap(s => s.lessons);
        }
        return (course.sections || []).flatMap(s => s.lessons);
    }, [course]);
    
    const allWeeks = useMemo(() => course?.modules?.flatMap(m => m.sections) || [], [course?.modules]);

     const progressData = useMemo(() => {
        if (!course || allLessons.length === 0) return null;

        const totalLessons = allLessons.length;
        const completedCount = completedLessons.length;
        const percentage = totalLessons > 0 ? (completedCount / totalLessons) * 100 : 0;

        let unitTitle = 'Course Completed!';
        let unitLessonCount = 0;

        // Find the first week that is not fully complete (lessons + quiz)
        let nextWeek: CourseSection | undefined;
        for (const week of allWeeks) {
            const allLessonsInWeekDone = week.lessons.every(l => completedLessons.includes(l.id));
            const quizDone = !week.quiz || !!completedQuizzes[week.quiz.id];
            if (!allLessonsInWeekDone || !quizDone) {
                nextWeek = week;
                break;
            }
        }

        if (nextWeek) {
            const parentModule = course.modules?.find(m => m.sections.some(s => s.id === nextWeek!.id));
            if (parentModule) {
                unitTitle = parentModule.title;
                unitLessonCount = parentModule.sections.reduce((acc, s) => acc + s.lessons.length, 0);
            } else {
                unitTitle = nextWeek.title;
                unitLessonCount = nextWeek.lessons.length;
            }
        }

        return {
            totalLessons,
            completedCount,
            percentage,
            unitTitle,
            unitLessonCount,
        };
    }, [course, allLessons, completedLessons, completedQuizzes, allWeeks]);


    if (!course) {
        return <div className="p-6"><EmptyState title="Course Not Found" message="Sorry, we couldn't find the course you're looking for." /></div>;
    }
    
    return (
        <div className="max-w-7xl mx-auto py-2 px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Main Content Column */}
                <div className="lg:col-span-2 space-y-8">
                    <div>
                        <h1 className="text-4xl font-extrabold text-gray-900 tracking-tight">{course.title}</h1>
                        <img src={course.imageUrl} alt={course.title} className="mt-6 w-full h-auto max-h-96 object-cover rounded-2xl shadow-lg" />
                    </div>

                    <Card className="p-6 space-y-6">
                        <h2 className="text-2xl font-bold">About This Course</h2>
                        <div className="prose prose-sm max-w-none text-gray-600 space-y-4">
                            {course.overview?.split('\n\n').map((para, i) => <p key={i}>{para}</p>)}
                        </div>
                    </Card>

                    <Card className="p-6 space-y-6">
                        <h2 className="text-2xl font-bold mb-4">What You Will Learn</h2>
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 text-sm text-gray-700">
                            {(course.whatYoullLearn || []).map(item => (
                                <li key={item} className="flex items-start gap-3">
                                    <CheckIcon />
                                    <span>{item}</span>
                                </li>
                            ))}
                        </ul>
                    </Card>
                    
                    <CourseCurriculum 
                        course={course}
                        onLessonClick={setSelectedLesson}
                        onQuizClick={setActiveQuiz}
                        userMode={auth?.currentUser?.mode}
                        completedLessons={completedLessons}
                        completedQuizzes={completedQuizzes}
                        allLessons={allLessons}
                    />
                                    
                    <InstructorCard instructorId={course.instructorId} />

                    <AskDoubtsCard course={course} lessons={allLessons} />
                </div>
                {/* Sticky Column (Right on large screens) */}
                <div className="lg:col-span-1">
                    <div className="sticky top-8 space-y-8">
                         {progressData && allLessons.length > 0 && (
                            <Card className="p-6">
                                <h3 className="text-2xl font-bold mb-4">Course Progress</h3>
                                <div className="space-y-3">
                                    <div className="flex justify-between items-center text-sm font-medium text-gray-600">
                                        <span>Progress</span>
                                        <span>{progressData.completedCount} of {progressData.totalLessons} lessons completed</span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-2">
                                        <div 
                                            className="bg-gradient-to-r from-red-500 to-yellow-400 h-2 rounded-full transition-all duration-500" 
                                            style={{ width: `${progressData.percentage}%` }}
                                        ></div>
                                    </div>
                                    {progressData.unitTitle && (
                                        <div className="pt-3 border-t border-gray-200 mt-4">
                                            <p className="text-sm text-gray-500">{progressData.completedCount === progressData.totalLessons ? 'Congratulations!' : 'Next up:'}</p>
                                            <p className="font-semibold text-gray-800">{progressData.unitTitle}</p>
                                            {progressData.unitLessonCount > 0 && <p className="text-xs text-gray-500">{progressData.unitLessonCount} lessons</p>}
                                        </div>
                                    )}
                                </div>
                            </Card>
                        )}

                        <Card className="p-6">
                            <h3 className="text-2xl font-bold mb-4">Program Features</h3>
                            <ul className="space-y-3 text-sm text-gray-700">
                                {course.features && Object.entries(course.features).map(([key, value]) => {
                                    let capitalizedKey = key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1');
                                    if(key === 'videoHours') capitalizedKey = 'Video';

                                    return (
                                        <li key={key} className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                {featuresIcons[key]}
                                                <span>{capitalizedKey}</span>
                                            </div>
                                            <strong className="font-semibold">{value} {key === 'videoHours' ? 'hours' : (key === 'enrolled' ? 'students' : '')}</strong>
                                        </li>
                                    )
                                })}
                            </ul>
                        </Card>

                        <Card className="p-6">
                            <h3 className="text-2xl font-bold mb-4">Includes</h3>
                            <ul className="space-y-3 text-sm text-gray-700">
                                {(course.includes || []).map(item => {
                                        const iconKey = Object.keys(includesIcons).find(key => item.toLowerCase().includes(key));
                                    return (
                                        <li key={item} className="flex items-center gap-3">
                                            {iconKey ? includesIcons[iconKey] : <CheckIcon />}
                                            <span>{item}</span>
                                        </li>
                                    )
                                })}
                            </ul>
                        </Card>
                    </div>
                </div>
            </div>

            {selectedLesson && <VideoPlayerModal lesson={selectedLesson} onClose={() => setSelectedLesson(null)} onLessonComplete={handleLessonComplete} />}
            {activeQuiz && <SectionQuizModal quiz={activeQuiz} onClose={() => setActiveQuiz(null)} onQuizComplete={handleQuizComplete} />}
        </div>
    );
};

export default CourseDetailPage;